/**
 * @file messageService.js
 * @description Service for handling notification messages using the actual API endpoints
 */

import { individualApiHttp, systemHttp } from './httpService';
import { format } from 'date-fns';
/**
 * Retrieve the tenant ID from local storage.
 * @returns {string|null} The tenant ID or null if not found.
 */
const getTenantId = () => +window.localStorage.getItem('tenantId');

/**
 * Get notification messages with filtering and pagination
 * @param {Object} params - Query parameters
 * @param {string} params.search - Search text within message bodies and subject
 * @param {string} params.sort - Sort direction for sentDate
 * @param {number[]} params.categoryIds - Category IDs to filter by
 * @param {number[]} params.channelIds - Channel IDs to filter by
 * @param {Date} params.sentDateStart - Start date for filtering messages
 * @param {Date} params.sentDateEnd - End date for filtering messages
 * @param {string} params.timeZone - Time zone for filtering messages
 * @param {number} params.page - Page number for pagination
 * @param {number} params.pageSize - Number of items per page
 */
export const getNotificationMessages = async ({
    search = '',
    sort = 'sentDateDesc',
    categoryIds = [],
    channelIds = [],
    sentDateStart,
    sentDateEnd,
    timeZone,
    page = 1,
    pageSize = 10,
}) => {
    try {
        const queryParams = new URLSearchParams();

        if (search) {
            queryParams.append('search', search);
        }

        queryParams.append('sort', sort);

        categoryIds.forEach(id => {
            queryParams.append('categoryIds', id);
        });

        channelIds.forEach(id => {
            queryParams.append('channelIds', id);
        });

        if (sentDateStart) {
            queryParams.append(
                'sentDateStart',
                format(sentDateStart, 'yyyy-MM-dd'),
            );
        }
        if (sentDateEnd) {
            queryParams.append(
                'sentDateEnd',
                format(sentDateEnd, 'yyyy-MM-dd'),
            );
        }

        if (timeZone) {
            queryParams.append('timeZone', timeZone);
        }

        queryParams.append('page', page);
        queryParams.append('pageSize', pageSize);

        const response = await individualApiHttp.get(
            `/v2/individual/notification-message/all?${queryParams.toString()}`,
        );

        return response.data;
    } catch (error) {
        console.error('Error fetching notification messages:', error);
        throw error;
    }
};

/**
 * Get notification message details with filtering and pagination
 * @param {Object} params - Query parameters
 * @param {string} params.notificationScheduleId - Notification schedule ID to filter by
 * @param {string} params.search - Search text within individual first name, last name, and source ID
 * @param {number} params.page - Page number for pagination
 * @param {number} params.pageSize - Number of items per page
 */
export const getNotificationMessageDetails = async (
    notificationScheduleId,
    searchTerm = '',
    page,
    pageSize,
) => {
    try {
        const queryParams = new URLSearchParams();
        const tenantId = getTenantId();
        queryParams.append('tenantId', tenantId);
        queryParams.append('notificationScheduleId', notificationScheduleId);

        if (searchTerm) {
            queryParams.append('search', searchTerm);
        }

        if (page) {
            queryParams.append('page', page);
        }

        if (pageSize) {
            queryParams.append('pageSize', pageSize);
        }

        const response = await individualApiHttp.get(
            `/v2/individual/notification-message/details?${queryParams.toString()}`,
        );

        return response.data;
    } catch (error) {
        console.error('Error fetching notification messages:', error);
        throw error;
    }
};

/**
 * Get notification categories
 * @returns {Promise<Array>} Array of notification categories
 */
export const getNotificationCategories = async () => {
    try {
        const response = await systemHttp.get(
            '/v2/system/notification-category',
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching notification categories:', error);
        throw error;
    }
};

/**
 * Get notification channels
 * @returns {Promise<Array>} Array of notification channels
 */
export const getNotificationChannels = async () => {
    try {
        const response = await systemHttp.get(
            '/v2/system/notification-channel',
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching notification channels:', error);
        throw error;
    }
};

/**
 * Get push destination types
 * @returns {Promise<Array>} Array of push destination types
 */
export const getPushDestinationTypes = async () => {
    try {
        const response = await systemHttp.get('/v2/system/push-destination-type');
        return response.data;
    } catch (error) {
        console.error('Error fetching push destination types:', error);
        throw error;
    }
};
